import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { useArtifactDetail } from "@/api/artifacts";
import { ArtifactDownloadButton } from "@/components/artifacts/ArtifactDownloadButton";
import { MoleculeViewerPanel } from "@/components/viz/MoleculeViewerPanel";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * One artifact: its catalog metadata, its downloadable payloads, and — when
 * its TYPE declares a structure — a 3D view of it.
 *
 * This route is the mount point the viewer needed and the portal did not have.
 * `RunArtifactCard` has linked at an artifact detail since B1 (via
 * `/artifacts?focus=…`, which the list page silently ignored), and
 * flowstack#534's D-6 named the artifact-detail route as where a viewer
 * belongs. It is also the right shape for the COLLABORATE pillar: the URL
 * carries the content hash, so "look at this molecule" is a link a teammate
 * can open, not a click path to describe.
 *
 * The page is deliberately a reader. It lists and renders what the catalog
 * already holds; it computes nothing.
 */
export function ArtifactDetailPage(): JSX.Element {
  const { artifact_id: artifactId } = useParams<{ artifact_id?: string }>();
  const { data: artifact, isLoading, isError } = useArtifactDetail(artifactId ?? null);

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" className="p-6 text-sm text-muted-foreground">
        Loading artifact…
      </div>
    );
  }

  if (isError || !artifact) {
    return (
      <Card elevation="raised">
        <CardHeader>
          <CardTitle>Could not load this artifact</CardTitle>
          <CardDescription>
            The catalog has no artifact at <span className="font-mono">{artifactId}</span> for your
            tenant, or the server returned an error.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const payloads = artifact.payloads ?? [];

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Link
          to="/artifacts"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
        >
          <ArrowLeft className="h-3 w-3" aria-hidden />
          All artifacts
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">{artifact.typed_object}</h1>
        <p className="break-all font-mono text-xs text-muted-foreground">{artifact.content_hash}</p>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
        <Field label="Schema version">v{artifact.schema_version}</Field>
        <Field label="Created">{new Date(artifact.created_at).toLocaleString()}</Field>
        <Field label="Project">
          <Link to={`/projects/${artifact.project_slug}`} className="hover:underline">
            {artifact.project_slug}
          </Link>
        </Field>
        <Field label="Scientific status">
          {artifact.scientific_status ?? <span className="text-muted-foreground">—</span>}
        </Field>
      </dl>

      <MoleculeViewerPanel artifact={artifact} />

      <section className="space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-sm font-medium text-foreground">Payload files</h2>
          <ArtifactDownloadButton contentHash={artifact.content_hash} />
        </div>
        {payloads.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            This artifact is JSON-only — its content lives in the catalog record, with no separate
            payload file.
          </p>
        ) : (
          <ul className="divide-y rounded-md border text-sm">
            {payloads.map((p) => (
              <li key={p.payload_name} className="flex items-center justify-between px-3 py-2">
                <span className="font-mono text-xs">{p.payload_name}</span>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {p.size_bytes === null ? "—" : `${p.size_bytes} B`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}
